import type { PhotoRecord } from "@/lib/catalog"
import { getExportedFilename } from "../editor/photo-exporting"
import { insertWordInFilename } from "../editor/filename-utils"

export function getPublishFilenames(photos: PhotoRecord[]) {
	const photosByBaseFilename = new Map<string, PhotoRecord[]>()
	// Publish filenames are used as Pixieset identity keys, so dedupe order must not
	// depend on the user's current sort or publish selection.
	const sortedPhotos = [...photos].sort(
		(a, b) => a.fileName.localeCompare(b.fileName) || a.id.localeCompare(b.id),
	)

	for (const photo of sortedPhotos) {
		const filename = getExportedFilename({
			photo,
			filenameType: "original",
			fileExtension: "jpg",
			trimSpaces: true,
		})
		if (!filename) continue

		const photosWithBaseFilename = photosByBaseFilename.get(filename)
		if (photosWithBaseFilename) {
			photosWithBaseFilename.push(photo)
		} else {
			photosByBaseFilename.set(filename, [photo])
		}
	}

	const usedFilenames = new Set<string>()
	const filenames: Record<string, string> = {}
	for (const [baseFilename, duplicatePhotos] of photosByBaseFilename) {
		for (const photo of duplicatePhotos) {
			const candidateFilename =
				duplicatePhotos.length === 1
					? baseFilename
					: insertWordInFilename(baseFilename, `-${getOriginalExtension(photo)}`)
			let filename = candidateFilename

			let duplicateIndex = 2
			while (usedFilenames.has(filename)) {
				filename = insertWordInFilename(candidateFilename, `-${duplicateIndex}`)
				duplicateIndex += 1
			}

			usedFilenames.add(filename)
			filenames[photo.id] = filename
		}
	}

	return filenames
}

function getOriginalExtension(photo: PhotoRecord) {
	const dotIndex = photo.fileName.lastIndexOf(".")
	if (dotIndex === -1 || dotIndex === photo.fileName.length - 1) {
		return "original"
	}

	return photo.fileName
		.substring(dotIndex + 1)
		.toLowerCase()
		.replace(/\s+/g, "")
}
